"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { usePrefersReducedMotion } from "@/components/ui/design-system/use-reduced-motion";
import {
  classifyPlayFailure,
  feedKeyAction,
  POOL_SIZE,
  pickActiveIndex,
  poolSlot,
  shouldLoadMore,
  stepIndex,
  VISIBILITY_THRESHOLDS,
} from "./utils";

/**
 * Marks every place the feed can rest: each video, and the loader after them.
 * Its value is the stop's index, which is what the observer reports back.
 */
export const STOP_ATTRIBUTE = "data-feed-stop";

/**
 * Whether a key belongs to the feed: pressed with focus somewhere in the
 * feed's own panel, or with nothing in particular focused. Not when it was
 * typed into a field, or into another dialog laid over the feed.
 */
function keysAreTheFeeds(
  target: EventTarget | null,
  scroller: HTMLElement | null,
): boolean {
  if (!(target instanceof HTMLElement)) return true;
  if (target === document.body) return true;
  if (
    target.isContentEditable ||
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  ) {
    return false;
  }
  const panel = scroller?.closest<HTMLElement>('[role="dialog"]');
  return panel ? panel.contains(target) : true;
}

/**
 * The feed's three video elements, made once per opening and never rendered
 * by React: each slide moves the one it is given into itself.
 *
 * THREE, AND RECYCLED, because of iOS Safari, where most creators watch.
 * WebKit allows sound only on an element that has been played inside a tap,
 * and it remembers that per element. One `<video>` per slide meant every
 * swipe landed on an element no tap had touched, so the second video came up
 * muted and the creator had to tap Unmute on every swipe. With a pool, the
 * opening tap plays all three, and every later video plays on one of them.
 * Three because slide `i` takes element `i % 3`, so the current video and
 * its two neighbours never share one.
 */
function createPool(): HTMLVideoElement[] {
  return Array.from({ length: POOL_SIZE }, () => {
    const video = document.createElement("video");
    // The attribute, which iOS reads; without it a play goes full screen.
    video.setAttribute("playsinline", "");
    video.loop = true;
    video.preload = "auto";
    return video;
  });
}

/**
 * The feed's state and its wiring to the browser: which video is current,
 * the one sound setting, a paused video, playback, the keyboard, and the
 * request for the next page.
 *
 * Lives inside the dialog's content, so every opening starts clean: at the
 * tapped video, trying for sound again, with a fresh pool.
 */
export function useVideoFeed({
  sources,
  startIndex,
  open,
  hasMore,
  loadingMore,
  onEndReached,
}: {
  /** Each video's file, in feed order; null for one with no file. */
  sources: readonly (string | null)[];
  startIndex: number;
  /** False through the closing animation, so nothing plays behind it. */
  open: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  onEndReached?: () => void;
}) {
  const count = sources.length;
  // The loader is a stop too, so the feed can rest on it while a page loads.
  const stops = count + (hasMore ? 1 : 0);
  const reducedMotion = usePrefersReducedMotion();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [pool] = useState(createPool);

  const [active, setActive] = useState(startIndex);
  const activeRef = useRef(startIndex);
  /**
   * Sound on to begin with: the tap that opened the feed is a user gesture,
   * which is what browsers ask for before playing with sound. If the browser
   * refuses anyway the feed drops to muted and says so (`soundBlocked`).
   */
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);
  const [soundBlocked, setSoundBlocked] = useState(false);
  /** The video the viewer paused, by index, so moving on clears it. */
  const [pausedIndex, setPausedIndex] = useState<number | null>(null);
  const paused = pausedIndex === active;
  /** The video count the last page request was made at. */
  const [requestedAt, setRequestedAt] = useState<number | null>(null);

  const latestEndReached = useRef(onEndReached);
  latestEndReached.current = onEndReached;
  const latestSources = useRef(sources);
  latestSources.current = sources;

  /** The element a stop plays on; none for the loader. */
  const elementAt = useCallback(
    (index: number) => (index < count ? pool[poolSlot(index)] : null),
    [count, pool],
  );

  /**
   * The element a stop's own `play()` will unlock inside the current tap, so
   * `unlockOthers` can leave it alone. None for a still: it plays nothing,
   * so every element, the one in its slot included, is unlocked as an other.
   */
  const playingElementAt = useCallback(
    (index: number) => (latestSources.current[index] ? elementAt(index) : null),
    [elementAt],
  );

  const setPoolMuted = useCallback(
    (next: boolean) => {
      mutedRef.current = next;
      for (const video of pool) video.muted = next;
    },
    [pool],
  );

  const play = useCallback(
    (index: number) => {
      // A stop with no file of its own (a still, or a video with none) plays
      // nothing. Its pooled element may still hold a neighbour's file, and
      // playing that would be sound from a video nobody is looking at.
      if (!latestSources.current[index]) return;
      const video = elementAt(index);
      if (!video?.hasAttribute("src")) return;
      video.muted = mutedRef.current;
      video.play().catch((error: DOMException | undefined) => {
        // Sound was refused. Play muted instead, and only if this is still
        // the video on screen: the viewer may have swiped on while it was
        // asked.
        if (classifyPlayFailure(error?.name) !== "blocked" || video.muted) {
          return;
        }
        setPoolMuted(true);
        setMuted(true);
        setSoundBlocked(true);
        if (activeRef.current !== index) return;
        video.play().catch(() => {});
      });
      // An interrupted play is the normal result of swiping past a video
      // that was still loading, and a broken file reports itself through the
      // element's `error` event, which the slide shows.
    },
    [elementAt, setPoolMuted],
  );

  /**
   * Plays and at once stops every pooled element but the current one, which
   * is how WebKit's per-element sound restriction is lifted: by a `play()`
   * inside a gesture, whatever becomes of it. Called only from a tap, or in
   * the same task as one, and before anything awaits.
   *
   * Including an element with no file yet: at the first or last video one of
   * the three has nothing to hold, and it is the one the next swipe needs.
   */
  const unlockOthers = useCallback(
    (current: HTMLVideoElement | null) => {
      for (const video of pool) {
        if (video === current) continue;
        video.play().catch(() => {});
        video.pause();
        if (video.currentTime > 0) video.currentTime = 0;
      }
    },
    [pool],
  );

  // Opens on the tapped video, already in place. Before paint, so the feed
  // never shows the first video and then jumps.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const stop = scroller?.querySelector<HTMLElement>(
      `[${STOP_ATTRIBUTE}="${startIndex}"]`,
    );
    if (scroller && stop) scroller.scrollTop = stop.offsetTop;
  }, []);

  // The opening tap's turn. A layout effect, after the slides have taken
  // their elements and files, runs in the same task as that tap.
  useLayoutEffect(() => {
    unlockOthers(playingElementAt(activeRef.current));
  }, []);

  // Playback follows the current video, in a layout effect for the same
  // reason: the first play is the one the opening tap pays for.
  const activeSource = sources[active] ?? null;
  useLayoutEffect(() => {
    const current = open ? elementAt(active) : null;
    for (const video of pool) {
      if (video === current) continue;
      video.pause();
      // Coming back to a video starts it over, as every feed does.
      if (video.currentTime > 0) video.currentTime = 0;
    }
    if (!current || !activeSource) return;
    if (paused) current.pause();
    else play(active);
  }, [active, activeSource, elementAt, open, paused, play, pool]);

  // Nothing keeps downloading once the feed is gone.
  useEffect(
    () => () => {
      for (const video of pool) {
        video.pause();
        video.removeAttribute("src");
        video.load();
      }
    },
    [pool],
  );

  // The most visible stop is the current one.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || typeof IntersectionObserver === "undefined") return;
    const ratios = new Map<number, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          ratios.set(
            Number(entry.target.getAttribute(STOP_ATTRIBUTE)),
            entry.intersectionRatio,
          );
        }
        const next = pickActiveIndex(ratios, activeRef.current);
        if (next === activeRef.current) return;
        activeRef.current = next;
        setActive(next);
        setPausedIndex(null);
      },
      { root: scroller, threshold: VISIBILITY_THRESHOLDS },
    );
    for (const stop of scroller.querySelectorAll(`[${STOP_ATTRIBUTE}]`)) {
      observer.observe(stop);
    }
    return () => observer.disconnect();
  }, [stops]);

  // Asks once per page: a request that failed is retried from the loader's
  // own button, not in a loop.
  useEffect(() => {
    if (!open || requestedAt === count) return;
    if (!shouldLoadMore({ active, count, hasMore, loading: loadingMore })) {
      return;
    }
    setRequestedAt(count);
    latestEndReached.current?.();
  }, [active, count, hasMore, loadingMore, open, requestedAt]);

  const retryLoadMore = useCallback(() => {
    setRequestedAt(count);
    latestEndReached.current?.();
  }, [count]);

  const goTo = useCallback(
    (index: number) => {
      const scroller = scrollerRef.current;
      const stop = scroller?.querySelector<HTMLElement>(
        `[${STOP_ATTRIBUTE}="${index}"]`,
      );
      if (!scroller || !stop) return;
      scroller.scrollTo({
        top: stop.offsetTop,
        behavior: reducedMotion ? "auto" : "smooth",
      });
    },
    [reducedMotion],
  );

  const step = useCallback(
    (delta: 1 | -1) => goTo(stepIndex(activeRef.current, delta, stops)),
    [goTo, stops],
  );

  // A tap on the video is a gesture too, so it unlocks the pool as it
  // pauses or resumes, and plays from inside the tap rather than waiting
  // for the render.
  const togglePlay = useCallback(() => {
    const index = activeRef.current;
    const current = playingElementAt(index);
    // A still has nothing to pause; Space on one only unlocks the pool.
    if (current) {
      const resuming = pausedIndex === index;
      setPausedIndex(resuming ? null : index);
      if (resuming) play(index);
      else current.pause();
    }
    unlockOthers(current);
  }, [pausedIndex, play, playingElementAt, unlockOthers]);

  // Set on the elements here as well as through state, so unmuting happens
  // inside the tap: a browser that refused sound on open allows it then.
  const toggleMute = useCallback(() => {
    const next = !mutedRef.current;
    setPoolMuted(next);
    setMuted(next);
    setSoundBlocked(false);
    const index = activeRef.current;
    // Played again even when it is already playing: the `play()` inside the
    // tap is what lifts the restriction on this element for good.
    if (pausedIndex !== index) play(index);
    unlockOthers(playingElementAt(index));
  }, [pausedIndex, play, playingElementAt, setPoolMuted, unlockOthers]);

  // On the document rather than the panel: the dialog is modal, so every key
  // is the feed's while it is open, and the hook lives inside the panel it
  // would otherwise need a handle on. Escape stays the dialog's.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      const action = feedKeyAction(event.key);
      if (!action || event.altKey || event.ctrlKey || event.metaKey) return;
      // A dialog opened over the feed (a caption's "Save to…") has the keys
      // while it is up: j, k and Space typed into its search field are
      // letters, not steps.
      if (!keysAreTheFeeds(event.target, scrollerRef.current)) return;
      if (action === "toggle") {
        // A focused button answers Space itself.
        if (event.target instanceof HTMLButtonElement) return;
        event.preventDefault();
        togglePlay();
        return;
      }
      event.preventDefault();
      step(action === "next" ? 1 : -1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, step, togglePlay]);

  return {
    scrollerRef,
    /** The pooled element slide `index` plays on. */
    elementFor: (index: number) => pool[poolSlot(index)],
    active,
    stops,
    muted,
    soundBlocked,
    paused,
    /** The page request failed and is waiting for the viewer to retry. */
    loadStalled: hasMore && !loadingMore && requestedAt === count,
    retryLoadMore,
    step,
    togglePlay,
    toggleMute,
  };
}
